
<template>
  <v-menu
    :close-on-content-click="false"
    transition="slide-y-transition"
  >
    <template #activator="{ props }">
      <v-btn
        v-if="!isListening"
        v-bind="props"
        @click="startMic"
      >
        <v-icon icon="fasldr fa-play" />
      </v-btn>
      <v-btn
        v-if="isListening"
        v-bind="props"
        @click="stopMic"
      >
        <v-icon icon="fasldr fa-stop" />
      </v-btn>
    </template>

    <v-card
      width="100%"
      color="primary"
      variant="tonal"
      class="mainpanel"
    >
      <v-card-title class="bg-primary d-flex justify-space-between">
        Chord Detector
      </v-card-title>
      <v-card-text>
        <div class="keyboard">
          <div
            v-for="key in keyboard"
            :key="key.note"
            class="key"
            :class="[
              key.type,
              highlighted.includes(key.note) ? 'active' : ''
            ]"
          >
            {{ key.note }}
          </div>
        </div>
        Note: {{ detectedNote || "—" }}
      </v-card-text>
      <v-card-actions>
        Chord: <v-chip>{{ detectedChord || "—" }}</v-chip>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

const detectedNote = ref<string | null>(null);
const detectedChord = ref<string | null>(null);
const isListening = ref(false);

let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let buffer: Float32Array | null = null;
let rafId: number | null = null;
let stream: MediaStream | null = null;

const activeNotes = new Set<string>();

const stableFrames = new Map<string, number>();
const MIN_FRAMES = 4; // number of frames required to confirm a note

const keyboard = [
  { note: "C",  type: "white" },
  { note: "C#", type: "black" },
  { note: "D",  type: "white" },
  { note: "D#", type: "black" },
  { note: "E",  type: "white" },
  { note: "F",  type: "white" },
  { note: "F#", type: "black" },
  { note: "G",  type: "white" },
  { note: "G#", type: "black" },
  { note: "A",  type: "white" },
  { note: "A#", type: "black" },
  { note: "B",  type: "white" }
];

const highlighted = ref<string[]>([]);

const NOTES = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const A4 = 440;

const CHORDS: Record<string, string[]> = {
  "C": ["C","E","G"],
  "Cm": ["C","D#","G"],
  "C7": ["C","E","G","A#"],
  "D": ["D","F#","A"],
  "Dm": ["D","F","A"],
  "D7": ["D","F#","A","C"],
  "E": ["E","G#","B"],
  "Em": ["E","G","B"],
  "F": ["F","A","C"],
  "Fm": ["F","G#","C"],
  "G": ["G","B","D"],
  "Gm": ["G","A#","D"],
  "A": ["A","C#","E"],
  "Am": ["A","C","E"],
  "B": ["B","D#","F#"],
  "Bm": ["B","D","F#"]
};


function freqToNote(freq: number) {
  const semitones = Math.round(12 * Math.log2(freq / A4));
  const index = (semitones + 9 + 1200) % 12;
  return NOTES[index];
}

function detectChord(notes: string[]): string | null {
  for (const [name, triad] of Object.entries(CHORDS)) {
    const matches = triad.filter(n => notes.includes(n)).length;
    if (matches >= 3) return name;
  }
  return null;
}

function interpolatedPeak(freqData: Float32Array, index: number, sampleRate: number, fftSize: number) {
  const alpha = freqData[index - 1];
  const beta = freqData[index];
  const gamma = freqData[index + 1];

  const shift = 0.5 * (alpha - gamma) / (alpha - 2 * beta + gamma);
  const trueIndex = index + shift;

  return (trueIndex * sampleRate) / fftSize;
}

// function detectNotesFromFFT(analyser: AnalyserNode, sampleRate: number) {
//   const fftSize = analyser.fftSize;
//   const freqData = new Float32Array(fftSize);
//   analyser.getFloatFrequencyData(freqData);

//   const detected: string[] = [];

//   const maxMag = Math.max(...freqData);
//   const threshold = maxMag - 20;

//   for (let i = 2; i < fftSize - 2; i++) {
//     const mag = freqData[i];
//     if (mag < threshold) continue;

//     if (!(mag > freqData[i - 1] && mag > freqData[i + 1])) continue;

//     const freq = interpolatedPeak(freqData, i, sampleRate, fftSize);
//     if (freq < 50 || freq > 1500) continue;

//     const fundamental = freq / Math.round(freq / 80);
//     if (fundamental < 50 || fundamental > 1500) continue;

//     const note = freqToNote(fundamental);
//     if (!detected.includes(note)) detected.push(note);
//   }

//   return detected;
// }
function detectNotesFromFFT(analyser: AnalyserNode, sampleRate: number) {
  const fftSize = analyser.fftSize;
  const freqData = new Float32Array(fftSize);
  analyser.getFloatFrequencyData(freqData);

  const peaks: number[] = [];
  const maxMag = Math.max(...freqData);
  const threshold = maxMag - 25; // mild threshold

  // const detected: string[] = [];

  for (let i = 2; i < fftSize - 2; i++) {
    const magnitude = freqData[i];
    // if (magnitude < -threshold) continue;

    if (!(magnitude > freqData[i - 1] && magnitude > freqData[i + 1])) continue;

    const leftSlope = magnitude - freqData[i - 1];
    const rightSlope = magnitude - freqData[i + 1];
    if (leftSlope < 3 || rightSlope < 3) continue;

    peaks.push(i);

    // const freq = interpolatedPeak(freqData, i, sampleRate, fftSize);
    // if (freq < 50 || freq > 2000) continue;

    // const note = freqToNote(freq);
    // if (!detected.includes(note)) detected.push(note);
  }

  const freqs = peaks.map(i => interpolatedPeak(freqData, i, sampleRate, fftSize));

    const fundamentals = freqs.map(f => {
      let base = f;
      while (base > 200) base /= 2;
      return base;
    });

    const detected: string[] = [];
    fundamentals.forEach(f => {
      if (f < 50 || f > 1500) return;
      const note = freqToNote(f);
      if (!detected.includes(note)) detected.push(note);
    });

  return detected;
}

async function startMic() {
  stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false
    }
  });

  audioCtx = new AudioContext();
  analyser = audioCtx.createAnalyser();
  analyser.fftSize = 4096;

  const source = audioCtx.createMediaStreamSource(stream);
  source.connect(analyser);

  buffer = new Float32Array(analyser.fftSize);
  isListening.value = true;

  loop();
}

function stopMic() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;

  if (stream) {
    stream.getTracks().forEach(t => t.stop());
    stream = null;
  }

  if (audioCtx) {
    audioCtx.close();
    audioCtx = null;
  }

  analyser = null;
  buffer = null;

  detectedNote.value = null;
  detectedChord.value = null;
  activeNotes.clear();
  isListening.value = false;
}

function loop() {
  if (!analyser || !audioCtx) return;

  const rawNotes = detectNotesFromFFT(analyser, audioCtx.sampleRate);

  rawNotes.forEach(n => {
      stableFrames.set(n, (stableFrames.get(n) || 0) + 1);
    });

  for (const [note, count] of stableFrames) {
      if (!rawNotes.includes(note)) {
        const newCount = count - 1;
        if (newCount <= 0) stableFrames.delete(note);
        else stableFrames.set(note, newCount);
      }
    }

    const stableNotes = [...stableFrames.entries()]
      .filter(([_, count]) => count >= MIN_FRAMES)
      .map(([note]) => note);

    highlighted.value = stableNotes;
    detectedNote.value = stableNotes.join(", ");

    const chord = detectChord(stableNotes);
    detectedChord.value = chord;

    rafId = requestAnimationFrame(loop);
  }

//   if (notes.length > 0) {
//     notes.forEach(n => activeNotes.add(n));
//     detectedNote.value = notes.join(", ");
//     highlighted.value = notes;

//     const chord = detectChord([...activeNotes]);
//     detectedChord.value = chord;
//   }

//   setTimeout(() => activeNotes.clear(), 200);

//   rafId = requestAnimationFrame(loop);
// }

onBeforeUnmount(() => {
  stopMic();
});

</script>

<style scoped>
.keyboard {
  display: flex;
  position: relative;
  height: 120px;
  margin-top: 20px;
}

.key {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  font-size: 12px;
  padding-bottom: 5px;
  border: 1px solid #333;
  box-sizing: border-box;
  user-select: none;
}

.white {
  width: 30px;
  background: #fff;
  z-index: 1;
}

.black {
  width: 20px;
  height: 70px;
  background: #000;
  color: #fff;
  margin-left: -15px;
  z-index: 2;
}

.active.white {
  background: #ffd54f;
}

.active.black {
  background: #ffb300;
}
</style>
